import { Button } from '@otter-ai/den-components';
import type * as recall from '@otter-ai/recall-client';
import {
  GeneralButtonActionEvent,
  Screen,
  TemplateClickEvent,
  trackEvent,
} from '@otter-ai/telemetry';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { PATH_URL } from '@/constants/path-constants';
import { useSubscription } from '../../shared/hooks/useSubscription';
import {
  useCloneTemplate,
  useDeleteTemplate,
} from '../hooks/templateMutations';
import { createUrl, redirectUrl } from '../utils/template.utils';
import { CustomTemplatePaywall } from './CustomTemplatePaywall';
import { PaywallTemplateGallery } from './PaywallTemplateGallery';
import { TemplateListEmptyState } from './TemplateListEmptyState';
import { TemplateTable } from './TemplateTable';

export type TemplateListProps = {
  templates: recall.Template[];
  type: 'personal' | 'admin';
  canCreateTemplate?: boolean;
};

export function TemplateList(props: TemplateListProps) {
  const { templates, type, canCreateTemplate } = props;
  const { subscription } = useSubscription();
  const [showTemplateGallery, setTemplateGallery] = useState(false);
  const [showCustomTemplatePaywall, setShowCustomTemplatePaywall] =
    useState(false);
  const [defaultSelectedType, setDefaultSelectedType] = useState<
    string | undefined
  >();

  const router = useRouter();
  const templateRedirect = redirectUrl(type);
  const newTemplateUrl = createUrl(templateRedirect);
  const shouldLimitActions = subscription?.meeting_types?.at_limit;

  const cloneTemplate = useCloneTemplate(type);
  const deleteTemplate = useDeleteTemplate(type);

  const onTemplateClick = (template: recall.Template) => {
    if (template.permissions?.can_apply) {
      setDefaultSelectedType(undefined);
      const urlWithId = new URL(newTemplateUrl);
      urlWithId.searchParams.append('id', template.id.toString());
      router.push(urlWithId.toString());
    } else if (template.is_customized) {
      setDefaultSelectedType(undefined);
      setShowCustomTemplatePaywall(true);
    } else {
      setDefaultSelectedType(template.base_template_type);
      setTemplateGallery(true);
    }
  };

  return (
    <div className="py-6 mx-auto">
      {showTemplateGallery && (
        <PaywallTemplateGallery
          type={type}
          onClose={() => setTemplateGallery(false)}
          url={
            type === 'personal'
              ? redirectUrl(type)
              : PATH_URL.TEAM_TEMPLATE_BUILDER
          }
          screen={
            type === 'personal'
              ? Screen.userSettingsMyTemplates
              : Screen.workspaceSettingsWorkspaceTemplates
          }
          defaultSelectedType={defaultSelectedType}
        />
      )}
      {showCustomTemplatePaywall && (
        <CustomTemplatePaywall
          onClose={() => setShowCustomTemplatePaywall(false)}
          screen={
            type === 'admin'
              ? Screen.workspaceSettingsWorkspaceTemplates
              : Screen.userSettingsMyTemplates
          }
        />
      )}
      {templates?.length === 0 ? (
        <TemplateListEmptyState
          canCreateTemplate={canCreateTemplate}
          navigate={() => setTemplateGallery(true)}
          disabled={shouldLimitActions}
        />
      ) : (
        <>
          <div className="flex justify-between items-center">
            <div />
            {/* TODO(sleahy): Move the button to sit within the SettingsPageHeader */}
            {canCreateTemplate && (
              <Button
                className="my-6"
                onClick={() => {
                  setTemplateGallery(true);
                  trackEvent(
                    GeneralButtonActionEvent({
                      Screen:
                        type === 'admin'
                          ? 'workspaceSettingsWorkspaceTemplates'
                          : 'userSettingsMyTemplates',
                      UIElementID: 'newTemplate',
                    }),
                  );
                }}
                disabled={shouldLimitActions}
                data-tutorial="meeting-types-new-template-button"
              >
                New Template
              </Button>
            )}
          </div>
          <TemplateTable
            templates={templates}
            isAdmin={type === 'admin'}
            onClick={(template) => {
              onTemplateClick(template);
              trackEvent(
                TemplateClickEvent({
                  TemplateType: type,
                  TemplateId: template.id,
                  TemplateName: template.name,
                }),
              );
            }}
            onCopy={(template) => {
              cloneTemplate.mutate({
                templateId: template.id,
                templateName: template.name,
              });
            }}
            onDelete={(template) => {
              deleteTemplate.mutate({
                templateId: template.id,
                templateName: template.name,
              });
            }}
            atLimit={shouldLimitActions}
          />
        </>
      )}
    </div>
  );
}
